import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin control for the D+3 abandoned-checkout recovery email.
 * Read/updates `/api/admin/settings/recovery-email`.
 * Note: D+1 is always on; this toggles the optional discount follow-up.
 */
export function RecoverySettingsPanel({ token }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pct, setPct] = useState(10);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/settings/recovery-email`, { headers });
        setSettings(res.data);
        setPct(res.data.d3_discount_percent);
      } catch (err) {
        toast.error('Could not load recovery settings');
      } finally { setLoading(false); }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/settings/recovery-email`,
        { d3_enabled: enabled, d3_discount_percent: pct },
        { headers },
      );
      setSettings(res.data);
      toast.success(enabled ? 'D+3 recovery email enabled' : 'D+3 recovery email disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleSavePct = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/settings/recovery-email`,
        { d3_discount_percent: Number(pct) },
        { headers },
      );
      setSettings(res.data);
      toast.success('Discount percent saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally { setSaving(false); }
  };

  if (loading || !settings) {
    return (
      <div style={{ marginTop: 28, paddingTop: 28, borderTop: '1px solid var(--color-border)' }}>
        <p style={{ fontSize: 13, color: 'var(--color-muted)' }}>Loading recovery settings…</p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 28, paddingTop: 28, borderTop: '1px solid var(--color-border)' }}
         data-testid="recovery-settings-panel">
      <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-navy)', margin: '0 0 4px' }}>
        Abandoned-checkout recovery emails
      </h3>
      <p style={{ fontSize: 13, color: 'var(--color-muted)', marginBottom: 18 }}>
        D+1 recovery (plain reminder) runs automatically. Toggle D+3 to send a second-touch
        email with a discount code <strong>{settings.d3_discount_code}</strong> that auto-applies at Stripe checkout.
      </p>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: 16,
        background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 10,
      }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.d3_enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            disabled={saving}
            data-testid="recovery-d3-toggle"
            style={{ width: 18, height: 18 }}
          />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-navy)' }}>
            Send D+3 recovery email {settings.d3_enabled && <span style={{ color: '#059669' }}>· enabled</span>}
          </span>
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--color-muted)' }}>Discount</label>
          <input
            type="number"
            min={1}
            max={99}
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            disabled={saving}
            data-testid="recovery-d3-percent"
            style={{
              width: 60, padding: '6px 8px', fontSize: 13,
              border: '1px solid var(--color-border)', borderRadius: 6,
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--color-muted)' }}>%</span>
          <button
            onClick={handleSavePct}
            disabled={saving || Number(pct) === settings.d3_discount_percent}
            data-testid="recovery-d3-save-percent"
            className="btn-secondary"
            style={{ padding: '6px 12px', fontSize: 12 }}
          >
            Save
          </button>
        </div>
      </div>

      {settings.d3_enabled && !settings.d3_stripe_promo_code_id && (
        <p style={{ fontSize: 11, color: '#92400E', marginTop: 10 }}>
          ⚠ Stripe promotion code not yet seeded. Toggle OFF then ON again to retry seeding —
          requires STRIPE_SECRET_KEY to be live.
        </p>
      )}
      {settings.d3_stripe_promo_code_id && (
        <p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 10 }}>
          Stripe promo code ID: <code>{settings.d3_stripe_promo_code_id}</code>
        </p>
      )}
    </div>
  );
}
